import { useRef, useState } from "react";
import {
  useOperationsManagementAnimations,
  useNormalizeScroll,
} from "animations";
import {
  Article,
  Hero,
  ImageNavigation,
  IntroBox,
  LottieWrapper,
  Play,
  Quote,
  WGYB,
  YouTubeModal,
} from "components";
import { isDesktop, desktopLottie } from "utils/isDesktop";
import { LottieInt, RefInt } from "interfaces";
import {
  quoteBgOM,
  quotePersonOM,
  quoteTextureOM,
  videoPlaceholder,
} from "assets/images";
import styles from "./OperationsManagement.module.scss";
import boxStyles from "components/IntroBox/IntroBox.module.scss";

const OperationsManagement = () => {
  const ref: RefInt = useRef(null);

  const [upperOffset, setUpperOffset] = useState<number>(0);

  useNormalizeScroll();
  useOperationsManagementAnimations(ref, setUpperOffset);

  const lotties: Array<LottieInt> = [
      {
        target: "operations-management-lottie",
        lottie: desktopLottie
          ? "operations-management_desktop"
          : "operations-management_mobile",
        headlines: [
          <>
            More eyes in the sky. <br />
            Less downtime <br />
            on the ground.
          </>,
        ],
        titles: [
          <>
            Operations center monitors ongoing machine performance in real time.
          </>,
          <>
            Monitoring centers track location, hours, fuel, maintenance, and
            more.
          </>,
          <>Insights become expert alerts that help prevent future repairs.</>,
          <>
            If issues do occur, remote diagnostics can provide machine updates
            without a trip to the jobsite.
          </>,
          <>
            When technicians are needed, they arrive with necessary parts to
            make repairs in one trip.
          </>,
          <>Operations center helps head off problems before they occur.</>,
        ],
      },
    ],
    articleContent = [
      {
        title: "MACHINE HEALTH MONITORING",
        body: "Standard on most models, JDLink constantly monitors machine performance, which can be viewed by the owner or dealer in Operations Center.",
      },
      {
        title: "EXPERT ALERTS",
        body: "An industry exclusive, our Expert Alerts system analyzes data from thousands of machines at Machine Health Monitoring Centers to notify dealers of potential issues.",
      },
      {
        title: "REMOTE DIAGNOSTICS",
        body: "Monitoring specialists at your dealership use telematics and alert management tools to quickly react without having to visit your site, before you even know there's a problem.",
      },
    ];

  const [open, _setOpen] = useState(false),
    openVideoModal = () => {
      _setOpen(true);
    },
    closeVideoModal = () => {
      _setOpen(false);
    };

  return (
    <div ref={ref} id={styles["operations-management"]}>
      <IntroBox
        primary={
          <div className={boxStyles.primary}>
            <div className={`${boxStyles.placeholder} anim-placeholder`}>
              <div className={`${boxStyles.numbers}`}></div>
              <h4 className={`bold ticker`}>-</h4>
              <div className={`${boxStyles.numbers}`}></div>
              <h4 className={`bold ticker`}>%</h4>
            </div>
            <h4 className={`${boxStyles.right} bold ticker anim-primary`}>
              UNPLANNED DOWNTIME IS COMMON.
            </h4>
          </div>
        }
        secondary="TIME TO CHANGE THAT."
        sub="FORCONSTRUCTIONPROS.COM"
        target={[2, 0]}
        targetTwo={[3, 0]}
        unit="%"
      />
      <Hero
        title="OPERATIONAL INTELLIGENCE"
        content="We've dialed in jobsite efficiency to help you avoid downtime and make operational decisions based on real-time data. Machine monitoring and Connected Support keep you updated with machine and fleet performance, along with job progress. All to keep your business humming."
        bg="operations-management"
      />

      <LottieWrapper lotties={lotties} upperOffset={upperOffset} />

      <section className={styles["article"]}>
        <h2 className={`${styles.header} bold`}>
          MACHINES TALK. WE LISTEN. 24/7.
        </h2>
        <div className={styles.img}>
          <img
            src={videoPlaceholder}
            width={1246}
            height={696}
            alt="Machines Talk We Listen"
            // loading="lazy"
          />
          <Play
            href="https://www.youtube.com/embed/K_wSDfN3h1w"
            onClick={openVideoModal}
          />
        </div>
        {isDesktop && (
          <YouTubeModal
            href="https://www.youtube.com/embed/K_wSDfN3h1w"
            open={open}
            closeModal={closeVideoModal}
          />
        )}
      </section>
      <Article content={articleContent} threeUp />

      <Quote
        className="operations-management"
        quote="It‘s like having a guardian angel looking over your fleet."
        name="Larry Leclair"
        position="Equipment Manager, AJ Johns"
        img={{ src: quotePersonOM, width: 600, height: 400 }}
        bg={quoteBgOM}
        texture={{ src: quoteTextureOM, width: 366, height: 269 }}
        dark
        gap
      />

      <ImageNavigation />
      <WGYB />
    </div>
  );
};

export default OperationsManagement;
